import { useState, useEffect } from 'react';
import {
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Heading,
  HStack,
  Icon,
  Image,
  Link as ChakraLink,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useParams, Link } from 'react-router-dom';
import { FiAlertTriangle, FiArrowLeft, FiCheck, FiExternalLink, FiPaperclip, FiStar, FiX } from 'react-icons/fi';
import { useClaim, disputeClaim } from '../../hooks/useClaims';
import { useBounty } from '../../hooks/useBounties';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { callProcessClaimPayment, getPaymentErrorMessage } from '../../hooks/useStripe';
import { usePaymentSettings } from '../../hooks/useSettings';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { StatusBadge } from '../common/StatusBadge';
import { StarRating } from '../common/StarRating';
import { formatDate, RESEARCH_TYPES } from '../../utils/formatters';
import { Price } from '../common/Price';
import { markNotificationsAsReadForBounty } from '../../hooks/useNotifications';

const GRADIENT_BY_TYPE = {
  attribution: 'linear(to-br, purple.400, blue.500)',
  provenance: 'linear(to-br, blue.400, cyan.500)',
  identification: 'linear(to-br, teal.400, green.500)',
  technical: 'linear(to-br, cyan.400, blue.400)',
  heraldry: 'linear(to-br, orange.400, red.400)',
  archival: 'linear(to-br, yellow.400, orange.400)',
};

export default function ClaimDetail() {
  const { claimId } = useParams();
  const { currentUser } = useAuth();
  const { userData } = useUser();
  const { claim, loading: claimLoading } = useClaim(claimId);
  const { bounty } = useBounty(claim?.bountyId);
  const { feePercent } = usePaymentSettings();
  const toast = useToast();

  const { isOpen: isApproveOpen, onOpen: onApproveOpen, onClose: onApproveClose } = useDisclosure();
  const { isOpen: isDisputeOpen, onOpen: onDisputeOpen, onClose: onDisputeClose } = useDisclosure();

  const [rating, setRating] = useState(5);
  const [feedback, setFeedback] = useState('');
  const [disputeReason, setDisputeReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  // Mark notifications as read when claimant views a resolved claim
  useEffect(() => {
    if (
      claim &&
      currentUser?.uid === claim.claimantUid &&
      ['approved', 'rejected', 'disputed'].includes(claim.status)
    ) {
      markNotificationsAsReadForBounty(currentUser.uid, claim.bountyId);
    }
  }, [claim, currentUser?.uid]);

  if (claimLoading) return <LoadingSpinner message="Loading submission..." />;

  if (!claim) {
    return (
      <Container maxW="3xl" py={8} textAlign="center">
        <Heading size="md" mb={4}>Submission not found</Heading>
        <Button as={Link} to="/my-submissions" leftIcon={<Icon as={FiArrowLeft} />}>
          My Submissions
        </Button>
      </Container>
    );
  }

  const isOwner = currentUser?.uid === claim.bountyCreatorUid;
  const canReview = isOwner && claim.status === 'pending';

  const gradient = bounty
    ? (GRADIENT_BY_TYPE[bounty.researchType] || 'linear(to-br, gray.400, gray.600)')
    : 'linear(to-br, gray.400, gray.600)';
  const typeInfo = bounty
    ? (RESEARCH_TYPES[bounty.researchType] || { label: 'Research', color: 'gray' })
    : { label: 'Research', color: 'gray' };

  const handleApprove = async () => {
    setProcessing(true);
    try {
      const result = await callProcessClaimPayment(claimId, claim.bountyId, rating, feedback);
      if (result.paymentFailed) {
        toast({
          title: 'Submission approved, but payment failed',
          description: 'You will be manually invoiced. A payment issue has been created.',
          status: 'warning',
          duration: 8000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Submission approved!',
          description: 'Payment processed and reward released.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
      onApproveClose();
    } catch (err) {
      toast({ title: 'Error', description: getPaymentErrorMessage(err), status: 'error', duration: 8000, isClosable: true });
    }
    setProcessing(false);
  };

  const handleDispute = async () => {
    if (!disputeReason.trim()) return;
    setProcessing(true);
    try {
      await disputeClaim(claimId, disputeReason.trim(), currentUser.uid);
      toast({
        title: 'Dispute filed',
        description: 'An arbitration case has been opened. Our team will review.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
      onDisputeClose();
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 5000, isClosable: true });
    }
    setProcessing(false);
  };

  return (
    <Container maxW="5xl" py={6}>
      <VStack spacing={6} align="stretch">
        <Button
          as={Link}
          to={isOwner ? `/tasks/${claim.bountyId}` : '/my-submissions'}
          variant="ghost"
          leftIcon={<Icon as={FiArrowLeft} />}
          alignSelf="flex-start"
          size="sm"
        >
          {isOwner ? 'Back to Task' : 'My Submissions'}
        </Button>

        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} alignItems="start">
          {/* Main claim content */}
          <VStack spacing={4} align="stretch" gridColumn={{ lg: 'span 2' }}>
            {/* Header */}
            <Box bg={cardBg} rounded="xl" p={5} borderWidth={1} borderColor={borderColor}>
              <HStack justify="space-between" w="full" flexWrap="wrap" gap={2}>
                <VStack align="start" spacing={0}>
                  <Text fontWeight="semibold">
                    {currentUser?.uid === claim.claimantUid ? (
                      'Your Submission'
                    ) : (
                      <>
                        Submission by{' '}
                        <ChakraLink
                          as={Link}
                          to={`/users/${claim.claimantUid}`}
                          state={{ from: 'bounties' }}
                          _hover={{ color: 'orange.500', textDecoration: 'underline' }}
                        >
                          {claim.claimantName || claim.claimantAnonymizedUsername}
                        </ChakraLink>
                      </>
                    )}
                  </Text>
                  <Text fontSize="sm" color="gray.500">Submitted {formatDate(claim.submittedAt)}</Text>
                </VStack>
                <StatusBadge status={claim.status} />
              </HStack>
            </Box>

            {/* Research Findings */}
            <Box bg={cardBg} rounded="xl" p={6} borderWidth={1} borderColor={borderColor}>
              <VStack align="start" spacing={4}>
                <Heading size="sm">Research Findings</Heading>
                <Divider />
                <Text color="gray.700" whiteSpace="pre-wrap" wordBreak="break-word" lineHeight="tall" fontSize="sm">
                  {claim.researchFindings}
                </Text>
              </VStack>
            </Box>

            {/* Sources */}
            {claim.sources?.length > 0 && (
              <Box bg={cardBg} rounded="xl" p={6} borderWidth={1} borderColor={borderColor}>
                <Heading size="sm" mb={4}>Sources & References</Heading>
                <VStack align="stretch" spacing={2}>
                  {claim.sources.map((src, i) => (
                    <HStack key={i} spacing={2} align="start">
                      <Text fontSize="sm" color="gray.400" minW="20px">{i + 1}.</Text>
                      {src.startsWith('http') ? (
                        <ChakraLink href={src} isExternal color="blue.500" fontSize="sm" wordBreak="break-all">
                          {src} <Icon as={FiExternalLink} boxSize={3} />
                        </ChakraLink>
                      ) : (
                        <Text fontSize="sm">{src}</Text>
                      )}
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}

            {/* Attachments */}
            {claim.attachmentUrls?.length > 0 && (
              <Box bg={cardBg} rounded="xl" p={6} borderWidth={1} borderColor={borderColor}>
                <Heading size="sm" mb={4}>Supporting Documents</Heading>
                <VStack align="stretch" spacing={2}>
                  {claim.attachmentUrls.map((url, i) => (
                    <HStack key={i}>
                      <Icon as={FiPaperclip} color="gray.400" />
                      <ChakraLink href={url} isExternal color="blue.500" fontSize="sm">
                        Attachment {i + 1} <Icon as={FiExternalLink} boxSize={3} />
                      </ChakraLink>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}

            {/* Result banners */}
            {claim.status === 'approved' && (
              <Box bg="green.50" rounded="xl" p={6} borderWidth={1} borderColor="green.200">
                <VStack align="start" spacing={2}>
                  <HStack>
                    <Icon as={FiCheck} color="green.500" />
                    <Heading size="sm" color="green.700">Submission Approved</Heading>
                  </HStack>
                  {claim.autoApproved && (
                    <Text fontSize="xs" color="green.600">
                      This submission was auto-approved after the 14-day review period.
                    </Text>
                  )}
                  {claim.collectorRating && <StarRating rating={claim.collectorRating} />}
                  {claim.collectorFeedback && (
                    <Text fontSize="sm" color="green.700">{claim.collectorFeedback}</Text>
                  )}
                </VStack>
              </Box>
            )}

            {claim.status === 'approved' && claim.paymentFailed && isOwner && (
              <Box bg="orange.50" rounded="xl" p={6} borderWidth={1} borderColor="orange.200">
                <HStack spacing={3} align="start">
                  <Icon as={FiAlertTriangle} color="orange.500" mt="2px" flexShrink={0} />
                  <VStack align="start" spacing={1}>
                    <Heading size="sm" color="orange.700">Payment Failed</Heading>
                    <Text fontSize="sm" color="orange.700">
                      Automatic payment could not be processed. You will be manually invoiced for this task.
                      If you have questions, please contact support.
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            )}

            {claim.status === 'rejected' && (
              <Box bg="red.50" rounded="xl" p={6} borderWidth={1} borderColor="red.200">
                <HStack>
                  <Icon as={FiX} color="red.500" />
                  <Heading size="sm" color="red.700">Submission Rejected</Heading>
                </HStack>
              </Box>
            )}

            {claim.status === 'disputed' && (
              <Box bg="orange.50" rounded="xl" p={6} borderWidth={1} borderColor="orange.200">
                <VStack align="start" spacing={2}>
                  <HStack>
                    <Icon as={FiX} color="orange.500" />
                    <Heading size="sm" color="orange.700">Submission Disputed</Heading>
                  </HStack>
                  {claim.disputeReason && (
                    <Text fontSize="sm" color="orange.700">{claim.disputeReason}</Text>
                  )}
                  <Text fontSize="xs" color="orange.600">
                    This dispute is under review. Our team will contact both parties.
                  </Text>
                </VStack>
              </Box>
            )}

            {/* Owner review actions */}
            {canReview && (
              <HStack spacing={4} justify="flex-end" flexWrap="wrap" gap={2}>
                <Button
                  colorScheme="orange"
                  variant="outline"
                  leftIcon={<Icon as={FiX} />}
                  onClick={onDisputeOpen}
                >
                  Dispute
                </Button>
                <Button
                  colorScheme="green"
                  leftIcon={<Icon as={FiCheck} />}
                  onClick={onApproveOpen}
                  whiteSpace="normal"
                  textAlign="center"
                >
                  Approve & Release Reward
                </Button>
              </HStack>
            )}
          </VStack>

          {/* Sidebar: bounty preview */}
          {bounty && (
            <VStack spacing={4} align="stretch">
              <Box bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
                <Box h="120px" overflow="hidden" position="relative">
                  {bounty.thumbnailUrl ? (
                    <Image src={bounty.thumbnailUrl} alt={bounty.title} w="full" h="full" objectFit="cover" />
                  ) : (
                    <Box w="full" h="full" bgGradient={gradient} />
                  )}
                  <HStack position="absolute" top={2} left={2} right={2} justify="space-between">
                    <Badge
                      colorScheme={typeInfo.color}
                      bg={`${typeInfo.color}.100`}
                      color={`${typeInfo.color}.800`}
                      fontSize="xs"
                      px={2}
                      py={1}
                      rounded="md"
                    >
                      {typeInfo.label}
                    </Badge>
                    <Box bg="white" rounded="lg" px={2} py={1} boxShadow="sm">
                      <Text fontSize="sm" fontWeight="bold" color="green.600"><Price cents={bounty.rewardAmount} size="sm" /></Text>
                    </Box>
                  </HStack>
                </Box>
                <VStack align="stretch" spacing={3} p={4}>
                  <Text fontWeight="semibold" fontSize="sm" noOfLines={2}>{bounty.title}</Text>
                  <Text fontSize="xs" color="gray.500" noOfLines={3}>{bounty.description}</Text>
                  {!isOwner && bounty.creatorAnonymizedUsername && (
                    <HStack spacing={1} fontSize="xs" color="gray.500">
                      <Text>Posted by</Text>
                      <ChakraLink
                        as={Link}
                        to={`/users/${bounty.creatorUid}`}
                        color="orange.500"
                        _hover={{ textDecoration: 'underline' }}
                      >
                        {bounty.creatorAnonymizedUsername}
                      </ChakraLink>
                    </HStack>
                  )}
                  <Button
                    as={Link}
                    to={`/tasks/${claim.bountyId}`}
                    size="sm"
                    variant="outline"
                    colorScheme="orange"
                    rightIcon={<Icon as={FiExternalLink} />}
                  >
                    View Full Task
                  </Button>
                </VStack>
              </Box>
            </VStack>
          )}
        </SimpleGrid>
      </VStack>

      {/* Approve Modal */}
      <Modal isOpen={isApproveOpen} onClose={onApproveClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Approve Submission</ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Payment Summary */}
              <Box bg="gray.50" rounded="lg" p={4}>
                <Text fontWeight="medium" mb={2}>Payment Summary</Text>
                <VStack spacing={1} align="stretch" fontSize="sm">
                  <HStack justify="space-between">
                    <Text color="gray.600">Task Reward</Text>
                    <Text><Price cents={bounty?.rewardAmount} /></Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text color="gray.600">Platform Fee ({feePercent}%)</Text>
                    <Text><Price cents={Math.round((bounty?.rewardAmount || 0) * feePercent / 100)} /></Text>
                  </HStack>
                  <Divider my={1} />
                  <HStack justify="space-between" fontWeight="bold">
                    <Text>Total Charge</Text>
                    <Text><Price cents={Math.round((bounty?.rewardAmount || 0) * (1 + feePercent / 100))} /></Text>
                  </HStack>
                  {(() => {
                    const methods = userData?.stripePaymentMethods || [];
                    const card = methods.find(m => m.id === bounty?.stripePaymentMethodId) || methods[0];
                    return card ? (
                      <Text color="gray.500" fontSize="xs" mt={1}>
                        Charged to {card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : 'Card'} ending in {card.last4}
                      </Text>
                    ) : null;
                  })()}
                </VStack>
              </Box>

              <Text fontWeight="medium">Rate the quality of the research:</Text>
              <HStack spacing={2}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Icon
                    key={star}
                    as={FiStar}
                    boxSize={7}
                    cursor="pointer"
                    color={star <= rating ? 'yellow.400' : 'gray.300'}
                    fill={star <= rating ? 'yellow.400' : 'none'}
                    onClick={() => setRating(star)}
                    _hover={{ color: 'yellow.400' }}
                    transition="color 0.1s"
                  />
                ))}
              </HStack>
              <Text fontWeight="medium">Feedback (optional)</Text>
              <Textarea
                placeholder="Leave feedback for the researcher..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={3}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onApproveClose}>Cancel</Button>
            <Button colorScheme="green" onClick={handleApprove} isLoading={processing} loadingText="Processing payment...">
              Confirm & Pay
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Dispute Modal */}
      <Modal isOpen={isDisputeOpen} onClose={onDisputeClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Dispute Submission</ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Please explain why this submission does not meet your criteria or is otherwise invalid.
                An arbitration case will be opened and our team will review.
              </Text>
              <Textarea
                placeholder="Describe the issue with this submission..."
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                rows={4}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDisputeClose}>Cancel</Button>
            <Button
              colorScheme="orange"
              onClick={handleDispute}
              isLoading={processing}
              isDisabled={!disputeReason.trim()}
            >
              File Dispute
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
}
